import { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/workspace-bi.css';

// ─────────────────────────────────────────────────────────────────────────────
// TypeScript types
// ─────────────────────────────────────────────────────────────────────────────

interface WSNote {
  id: number;
  kind: 'note' | 'jira' | 'task' | 'risk';
  color?: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  z: number;
  ts: string;
  title?: string;
  body?: string;
  sourceId?: string;
  sourceData?: Record<string, unknown>;
}

interface WSConnector {
  id: number;
  fromId: number;
  toId: number;
  kind: 'leads-to' | 'blocks' | 'maps-to';
}

interface OrchStep {
  num: string;
  verb: string;
  what: string;
  who: string;
  when: string;
}

interface OrchPlan {
  problem: string;
  steps: OrchStep[];
  outcome: string;
}

interface ModalState {
  open: boolean;
  title: string;
  body: string;
  footer: string;
  size: string;
}

interface WSToast {
  id: number;
  msg: string;
  type: 'ok' | 'err' | 'info';
}

interface DragState {
  el: HTMLDivElement;
  offsetX: number;
  offsetY: number;
}

interface TLBarDrag {
  mode: 'move' | 'resize';
  stepIdx: number;
  stepNum: number;
  startX: number;
  initialLeft: number;
  initialWidth: number;
  dayWidth: number;
  el: HTMLDivElement;
  moved: boolean;
  tip: HTMLDivElement | null;
}

interface TLStepState {
  dayOffset: number;
  duration: number;
}

interface TLState {
  laneMode: string;
  steps: TLStepState[];
  nowTimer: ReturnType<typeof setInterval> | null;
}

interface ParsedWhen {
  date: Date;
  isStandby: boolean;
  raw: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const WS_STATE_KEY = 'atlas_workspace_v1';
const WS_COLORS = ['yellow', 'pink', 'blue', 'green', 'lavender', 'orange'];

function pbEsc(s: string | null | undefined): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _wsFmtDate(ts: string | undefined): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return ''; }
}

const WS_PRIORITY_ICO: Record<string, string> = {
  Highest: '<span style="color:#CD1316;font-weight:700">⇈</span>',
  High: '<span style="color:#E5493A;font-weight:700">↑</span>',
  Medium: '<span style="color:#E97F33;font-weight:700">═</span>',
  Low: '<span style="color:#2684FF;font-weight:700">↓</span>',
  Lowest: '<span style="color:#57A55A;font-weight:700">⇊</span>',
};

const WS_AVATAR_NAMES: Record<string, string> = {
  CM: 'C. Mendoza', BL: 'B. Liu', SP: 'S. Palmer', DR: 'D. Reyes', RN: 'S. Williamson', JH: 'J. Hartwell',
};

const WS_CONN_KINDS: Array<'leads-to' | 'blocks' | 'maps-to'> = ['leads-to', 'blocks', 'maps-to'];
const WS_CONN_LABELS: Record<string, string> = { 'leads-to': 'LEADS TO', blocks: 'BLOCKS', 'maps-to': 'MAPS TO' };

const WS_PICKER_LABELS: Record<string, { title: string; icon: string }> = {
  jira: { title: 'Add Jira ticket to workspace', icon: '<span class="jb-type-ico task" style="width:18px;height:18px;font-size:11px"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span></span>' },
  task: { title: 'Add task to workspace', icon: '<span style="display:inline-flex;width:16px;height:16px;border:1.5px solid var(--navy);border-radius:50%;align-items:center;justify-content:center;font-size:10px;color:var(--navy)"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span></span>' },
  risk: { title: 'Add risk to workspace', icon: '<span style="font-size:14px;color:#A32B33"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5L15 14H1z"/><path d="M8 6v4"/><circle cx="8" cy="12" r=".6" fill="currentColor"/></svg></span></span>' },
};

const WS_ORCH_SYS = `You are the ATLAS Orchestrator — an IT operations advisor. Given a list of workspace items (sticky notes, Jira tickets, tasks, risks), produce a course-of-action plan.

Output your response in EXACTLY this format:

PROBLEM
[1-2 sentence framing]

PLAN
1. [VERB IN ALL-CAPS] — [concrete action]
   WHO: [who does it]
   WHEN: [timeline]

(Produce 4-6 numbered steps.)

OUTCOME
[1 sentence describing the desired end state.]`;

const WS_DEMO_SCENARIO: Array<Record<string, unknown>> = [
  { kind: 'note', color: 'yellow', x: 40, y: 40, title: 'RAVEn query slowdown · Mendoza assigned', body: 'Federal flagged via Diana 0830 · SLA clock running · 4hr window · Mendoza pulled from sprint planning to triage' },
  { kind: 'note', color: 'pink', x: 40, y: 235, title: 'RAVEn ADE-3 packet · my team owes narrative', body: '15 days out · D. Reyes has TECS-Mod section · RAVEn section + risk crosswalk still on my plate' },
  { kind: 'note', color: 'blue', x: 40, y: 430, title: 'Decision needed', body: 'Sequencing: keep Mendoza on RAVEn latency triage (SLA at risk) or pull him into ADE-3 narrative drafting?' },
  { kind: 'jira', sourceId: 'ITPMS-213', x: 330, y: 40 },
  { kind: 'jira', sourceId: 'ITPMS-201', x: 330, y: 235 },
  { kind: 'task', sourceId: 'TK-006', x: 620, y: 40 },
  { kind: 'risk', sourceId: 'R-RAV-014', x: 620, y: 235 },
  { kind: 'risk', sourceId: 'R-HSI-007', x: 620, y: 430 },
];
const WS_DEMO_CONNECTORS = [
  { from: 0, to: 3, kind: 'leads-to' as const },
  { from: 1, to: 4, kind: 'maps-to' as const },
  { from: 6, to: 3, kind: 'maps-to' as const },
  { from: 7, to: 4, kind: 'blocks' as const },
  { from: 4, to: 5, kind: 'leads-to' as const },
];

// ─── Plan-strip timeline ───
const WS_STAGES: Record<string, { label: string; desc: string; color: string }> = {
  triage: { label: 'TRIAGE', desc: 'Immediate response · scope the problem', color: '#E5493A' },
  coordinate: { label: 'COORDINATE', desc: 'Stakeholder alignment · communication', color: '#3D6FB5' },
  execute: { label: 'EXECUTE', desc: 'Active delivery · contingency staging', color: '#C9A032' },
  verify: { label: 'VERIFY', desc: 'Checkpoint · closure · audit trail', color: '#2C9659' },
};
const WS_STAGE_ORDER = ['triage', 'coordinate', 'execute', 'verify'];
const WS_VERB_STAGE: Record<string, string> = {
  CONVENE: 'triage', TRIAGE: 'triage', ESCALATE: 'triage', ASSESS: 'triage', INVESTIGATE: 'triage',
  IDENTIFY: 'triage', DIAGNOSE: 'triage', LAUNCH: 'triage', STAND: 'triage', GATHER: 'triage', OPEN: 'triage',
  SEPARATE: 'coordinate', INFORM: 'coordinate', COORDINATE: 'coordinate', ALIGN: 'coordinate',
  BRIEF: 'coordinate', REQUEST: 'coordinate', NEGOTIATE: 'coordinate', DELEGATE: 'coordinate',
  NOTIFY: 'coordinate', SCHEDULE: 'coordinate', PLAN: 'coordinate', ANNOUNCE: 'coordinate',
  COMMUNICATE: 'coordinate', DECOUPLE: 'coordinate', TRACK: 'coordinate',
  EXECUTE: 'execute', DRIVE: 'execute', IMPLEMENT: 'execute', DEPLOY: 'execute',
  CONTINGENCY: 'execute', 'PRE-STAGE': 'execute', STAGE: 'execute', BUILD: 'execute',
  DEVELOP: 'execute', PROCURE: 'execute', ENGAGE: 'execute', OPERATE: 'execute', BATCH: 'execute',
  PREPARE: 'execute', RESOLVE: 'execute',
  CHECKPOINT: 'verify', VERIFY: 'verify', REVIEW: 'verify', CLOSE: 'verify',
  CONFIRM: 'verify', RETIRE: 'verify', VALIDATE: 'verify', AUDIT: 'verify',
  APPROVE: 'verify', SIGN: 'verify', COMPLETE: 'verify', SETTLE: 'verify', FINALIZE: 'verify',
};

function _wsDeriveStage(step: OrchStep): string {
  const verb = (step.verb || '').toUpperCase().trim();
  if (WS_VERB_STAGE[verb]) return WS_VERB_STAGE[verb];
  for (const v in WS_VERB_STAGE) { if (verb.includes(v)) return WS_VERB_STAGE[v]; }
  return 'execute';
}

const WS_OWNERS: Record<string, { label: string; desc: string }> = {
  pm: { label: 'PM (Steve)', desc: 'Self-driven · internal' },
  contractor: { label: 'CONTRACTOR (ITPMS)', desc: 'Steve Palmer · B. Liu · C. Mendoza · D. Reyes' },
  cor: { label: 'COR / FINANCE', desc: 'J. Hartwell · invoice · burn' },
  stakeholders: { label: 'STAKEHOLDERS / HQ', desc: 'OCIO · IRB · HSI HQ · M. Okonkwo' },
};
const WS_OWNER_ORDER = ['pm', 'contractor', 'stakeholders', 'cor'];

function _wsDeriveOwner(step: OrchStep): string {
  const who = (step.who || '').toLowerCase();
  if (/hsi\s*hq|stakeholder|okonkwo|hsia|director|\birb\b|\bocio\b|external|gao|\bvargas\b|\bdiana\b/.test(who)) return 'stakeholders';
  if (/palmer|liu|mendoza|reyes|contractor|\bdba\b|raven\s*dba|afis\s*dba/.test(who)) return 'contractor';
  if (/hartwell|\bcor\b|finance|invoice/.test(who)) return 'cor';
  return 'pm';
}

const WS_DOMAIN_TERMS = new Set([
  'AFIS', 'GAO', 'ITAR', 'ADE', 'ADE-3', 'COOP', 'IRB', 'HSI', 'OCIO', 'COR', 'RAVEN', 'RAVEN-FL', 'LIMS',
  'IDEAL', 'HSINET', 'POLYDS', 'DOCEX', 'CCHT-DFS', 'TTX', 'PIV', 'CVE', 'QBR', 'CCB', 'UAT', 'SLA', 'NEFL',
  'AFIS-FL', 'PRINT', 'LATENT', 'CELLEBRITE', 'ICAM', 'SSO', 'DBA',
]);

// ── Pure orchestrator-plan parsers ──
function _wsParseStepRefs(step: OrchStep): string[] {
  const text = (step.verb || '') + ' ' + (step.what || '') + ' ' + (step.who || '') + ' ' + (step.when || '');
  const matches = text.match(/\b(ITPMS-\d+|R-[A-Z]+-\d+|TK-\d+)\b/g) || [];
  return [...new Set(matches)];
}

function _wsParseStepKeywords(step: OrchStep): string[] {
  const text = (step.verb || '') + ' ' + (step.what || '') + ' ' + (step.who || '') + ' ' + (step.when || '');
  const out = new Set<string>();
  const tokens = text.match(/\b[A-Za-z][A-Za-z0-9-]{2,}\b/g) || [];
  for (const tok of tokens) { const upper = tok.toUpperCase(); if (WS_DOMAIN_TERMS.has(upper)) out.add(upper); }
  const hyphen = text.match(/\b[A-Z]{2,}-?\d+\b|\b[A-Z]+-\d+\b/g) || [];
  hyphen.forEach((t) => out.add(t));
  const COMMON = new Set(['Stand', 'Move', 'Schedule', 'Drive', 'Brief', 'Confirm', 'Pre-stage', 'Pre', 'Both', 'From', 'Then', 'With', 'That', 'This', 'These', 'Those', 'When', 'Where', 'While']);
  const caps = text.match(/\b[A-Z][a-z]{3,}\b/g) || [];
  for (const w of caps) { if (!COMMON.has(w)) out.add(w); }
  return Array.from(out);
}

function _wsOrchParse(text: string): OrchPlan {
  const out: OrchPlan = { problem: '', steps: [], outcome: '' };
  const lines = (text || '').split('\n');
  let mode: string | null = null;
  let cur: OrchStep | null = null;
  const flush = () => { if (cur) { out.steps.push(cur); cur = null; } };
  for (const raw of lines) {
    const t = raw.trim();
    if (!t) continue;
    if (/^PROBLEM\b/i.test(t)) { flush(); mode = 'problem'; continue; }
    if (/^PLAN\b/i.test(t)) { flush(); mode = 'plan'; continue; }
    if (/^OUTCOME\b/i.test(t)) { flush(); mode = 'outcome'; continue; }
    if (mode === 'problem') {
      out.problem += (out.problem ? ' ' : '') + t;
    } else if (mode === 'plan') {
      const step = t.match(/^(\d+)[.):]\s*([A-Z][A-ZÀ-ſ\s]+?)\s*[—\-–:]\s*(.+)/);
      if (step) {
        flush();
        cur = { num: step[1], verb: step[2].trim(), what: step[3].trim(), who: '', when: '' };
      } else {
        const who = t.match(/^WHO[:\s]+(.+)/i);
        const when = t.match(/^WHEN[:\s]+(.+)/i);
        if (who && cur) cur.who = who[1].trim();
        else if (when && cur) cur.when = when[1].trim();
        else if (cur) cur.what += ' ' + t;
      }
    } else if (mode === 'outcome') {
      out.outcome += (out.outcome ? ' ' : '') + t;
    }
  }
  flush();
  return out;
}

function _wsParseWhenDate(whenStr: string): ParsedWhen | null {
  if (!whenStr) return null;
  const lower = whenStr.toLowerCase();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isStandby = /\b(standby|trigger|if needed|contingen|standing by)\b/i.test(whenStr);
  const dateMatch = whenStr.match(/\b(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i);
  if (dateMatch) {
    const day = parseInt(dateMatch[1], 10);
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const month = months.indexOf(dateMatch[2].toLowerCase());
    const yr = now.getFullYear();
    const candidate = new Date(yr, month, day);
    if (candidate.getTime() < today.getTime() - 30 * 86400000) candidate.setFullYear(yr + 1);
    return { date: candidate, isStandby, raw: whenStr };
  }
  if (/\btomorrow\b/.test(lower)) { const d = new Date(today); d.setDate(d.getDate() + 1); return { date: d, isStandby, raw: whenStr }; }
  if (/\btoday\b|\bcob\b|\beod\b/.test(lower)) { return { date: today, isStandby, raw: whenStr }; }
  const hoursMatch = whenStr.match(/(\d+)\s*hours?/i);
  if (hoursMatch) { const hrs = parseInt(hoursMatch[1], 10); const d = new Date(today); d.setDate(d.getDate() + Math.ceil(hrs / 24)); return { date: d, isStandby, raw: whenStr }; }
  const daysMatch = whenStr.match(/(\d+)\s*days?/i);
  if (daysMatch) { const d = new Date(today); d.setDate(d.getDate() + parseInt(daysMatch[1], 10)); return { date: d, isStandby, raw: whenStr }; }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function WorkspacePage() {
  const [toasts, setToasts] = useState<WSToast[]>([]);
  const [modal, setModal] = useState<ModalState>({ open: false, title: '', body: '', footer: '', size: 'lg' });
  const apiRef = useRef<Record<string, (...args: unknown[]) => void>>({});
  const lastPlanRef = useRef<OrchPlan | null>(null);
  const toastIdRef = useRef(0);

  const showToast = useCallback((msg: string, type: 'ok' | 'err' | 'info' = 'info') => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const openMo = useCallback((title: string, body: string, footer: string, size = 'lg') => {
    setModal({ open: true, title, body, footer, size });
  }, []);

  const closeMo = useCallback(() => {
    setModal(prev => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    // ── Imperative board state ──
    let WS_NOTES: WSNote[] = [];
    let WS_CONNECTORS: WSConnector[] = [];
    let WS_NEXT_ID = 1;
    let WS_TOP_Z = 1;
    let WS_CONN_NEXT_ID = 1;
    let WS_SELECTED_COLOR = 'yellow';
    let WS_CONNECT_MODE = false;
    let WS_CONN_SOURCE: number | null = null;
    let WS_HIGHLIGHTED_STEP: number | null = null;
    let WS_DRAG: DragState | null = null;
    let _wsConnRaf: number | null = null;
    let WS_QC_COUNTER = 1;
    let WS_TL_STATE: TLState = { laneMode: 'stage', steps: [], nowTimer: null };
    let WS_TL_BAR_DRAG: TLBarDrag | null = null;

    // Data sources (local stubs for Atlas)
    const dsJira = (): Array<Record<string, unknown>> => (window as Record<string, unknown>).__WS_JIRA as Array<Record<string, unknown>> || [];
    const dsTasks = (): Array<Record<string, unknown>> => (window as Record<string, unknown>).__WS_TASKS as Array<Record<string, unknown>> || [];
    const dsRisks = (): Array<Record<string, unknown>> => (window as Record<string, unknown>).__WS_RISKS as Array<Record<string, unknown>> || [];

    // ── persistence ──
    function wsLoad() {
      try {
        const raw = localStorage.getItem(WS_STATE_KEY);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data && Array.isArray(data.notes)) {
          WS_NOTES = data.notes;
          WS_NEXT_ID = data.nextId || (WS_NOTES.reduce((m: number, n: WSNote) => Math.max(m, Number.isFinite(n.id) ? n.id : 0), 0) + 1);
          WS_TOP_Z = data.topZ || (WS_NOTES.reduce((m: number, n: WSNote) => Math.max(m, n.z || 1), 1));
          let idsBackfilled = false;
          WS_NOTES.forEach((n) => {
            if (!Number.isFinite(n.id)) { n.id = WS_NEXT_ID++; idsBackfilled = true; }
          });
          if (idsBackfilled) wsSave();
        }
        if (data && Array.isArray(data.connectors)) {
          WS_CONNECTORS = data.connectors;
          WS_CONN_NEXT_ID = data.connNextId || (WS_CONNECTORS.reduce((m: number, c: WSConnector) => Math.max(m, c.id || 0), 0) + 1);
        }
      } catch (e) { console.warn('workspace load failed', e); }
    }

    function wsSave() {
      try {
        localStorage.setItem(WS_STATE_KEY, JSON.stringify({
          notes: WS_NOTES, nextId: WS_NEXT_ID, topZ: WS_TOP_Z,
          connectors: WS_CONNECTORS, connNextId: WS_CONN_NEXT_ID, v: 1,
        }));
        const t = new Date();
        const stamp = String(t.getHours()).padStart(2, '0') + ':' + String(t.getMinutes()).padStart(2, '0');
        const lastEl = document.getElementById('ws-last-saved');
        if (lastEl) lastEl.textContent = stamp;
      } catch (e) { console.warn('workspace save failed', e); }
    }

    function _wsUpdateCounters() {
      const c = document.getElementById('ws-note-count');
      if (c) c.textContent = String(WS_NOTES.length);
    }

    // ── card builders ──
    function _wsBuildNote(n: WSNote, isNew: boolean): HTMLDivElement {
      const kind = n.kind || 'note';
      if (kind === 'jira') return _wsBuildJira(n, isNew);
      if (kind === 'task') return _wsBuildTask(n, isNew);
      if (kind === 'risk') return _wsBuildRisk(n, isNew);
      return _wsBuildSticky(n, isNew);
    }

    function _wsWireResize(el: HTMLDivElement) {
      const handle = el.querySelector('[data-resize="1"]');
      if (!handle) return;
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault(); e.stopPropagation();
        const id = parseInt(el.dataset.id!, 10);
        const n = WS_NOTES.find((x) => x.id === id);
        if (!n) return;
        const startX = e.clientX, startY = e.clientY;
        const startW = el.offsetWidth, startH = el.offsetHeight;
        el.classList.add('resizing');
        const onMove = (ev: MouseEvent) => {
          el.style.width = Math.max(190, startW + (ev.clientX - startX)) + 'px';
          el.style.height = Math.max(150, startH + (ev.clientY - startY)) + 'px';
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          el.classList.remove('resizing');
          n.w = el.offsetWidth; n.h = el.offsetHeight;
          _wsRenderConnectors();
          wsSave();
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });
    }

    function _wsBuildSticky(n: WSNote, isNew: boolean): HTMLDivElement {
      const el = document.createElement('div');
      el.className = 'ws-note c-' + (n.color || 'yellow') + (isNew ? ' is-new' : '');
      el.dataset.id = String(n.id);
      el.style.left = (n.x || 40) + 'px';
      el.style.top = (n.y || 40) + 'px';
      el.style.width = (n.w || 280) + 'px';
      if (n.h) el.style.height = n.h + 'px';
      el.style.zIndex = String(n.z || 1);
      const rot = ((n.id * 7) % 30 - 15) / 10;
      el.style.setProperty('--rot', rot + 'deg');
      el.innerHTML = `
        <div class="ws-note-bar" data-drag="1">
          <div class="ws-note-grip"><span></span><span></span><span></span></div>
          <button class="ws-note-color-cycle" title="Change color">${WS_COLORS.indexOf(n.color || 'yellow') + 1}</button>
          <button class="ws-note-x" title="Delete note" aria-label="Delete note"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></button>
        </div>
        <div class="ws-note-title" contenteditable="true" data-field="title" spellcheck="false"></div>
        <div class="ws-note-body" contenteditable="true" data-field="body" spellcheck="true"></div>
        <div class="ws-note-foot">
          <span>${_wsFmtDate(n.ts)}</span>
          <span>#${Number.isFinite(n.id) ? String(n.id).padStart(3, '0') : '???'}</span>
        </div>
        <div class="ws-note-resize" data-resize="1" title="Drag to resize"></div>`;
      el.querySelector('[data-field="title"]')!.textContent = n.title || '';
      el.querySelector('[data-field="body"]')!.textContent = n.body || '';
      // Wire event handlers
      el.querySelector('.ws-note-color-cycle')!.addEventListener('click', () => wsCycleColor(n.id));
      el.querySelector('.ws-note-x')!.addEventListener('click', () => wsDeleteNote(n.id));
      _wsWireNote(el);
      _wsWireResize(el);
      return el;
    }

    function _wsBuildJira(n: WSNote, isNew: boolean): HTMLDivElement {
      const t = (n.sourceData || {}) as Record<string, string>;
      const el = document.createElement('div');
      el.className = 'ws-note ws-card kind-jira' + (isNew ? ' is-new' : '');
      el.dataset.id = String(n.id);
      el.style.left = (n.x || 40) + 'px';
      el.style.top = (n.y || 40) + 'px';
      el.style.width = (n.w || 240) + 'px';
      el.style.zIndex = String(n.z || 1);
      el.style.setProperty('--rot', '0deg');
      const typeIco = '<span class="jb-type-ico ' + (t.type || 'task').toLowerCase() + '" style="width:14px;height:14px;font-size:9px">' + (t.type === 'Bug' ? 'B' : '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span>') + '</span>';
      const av = WS_AVATAR_NAMES[t.assignee] || t.assignee || '—';
      el.innerHTML = `
        <div class="ws-note-bar" data-drag="1">
          <div class="ws-note-grip"><span></span><span></span><span></span></div>
          <button class="ws-note-x" title="Remove from board" aria-label="Remove from board" style="color:#0747A6"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></button>
        </div>
        <div class="ws-card-h">${typeIco}<span class="src-id">${t.id || ''}</span><span class="h-spacer"></span>${WS_PRIORITY_ICO[t.priority] || ''} <span style="font-size:10px;color:var(--ink-45)">${t.priority || ''}</span></div>
        <div class="ws-card-bd">${pbEsc(t.summary || '')}</div>
        <div class="ws-card-foot"><span>${(t.status || '—').replace(/</g, '&lt;')}</span><span>${av}</span></div>`;
      el.querySelector('.ws-note-x')!.addEventListener('click', () => wsDeleteNote(n.id));
      _wsWireNote(el);
      return el;
    }

    function _wsBuildTask(n: WSNote, isNew: boolean): HTMLDivElement {
      const t = (n.sourceData || {}) as Record<string, string>;
      const el = document.createElement('div');
      el.className = 'ws-note ws-card kind-task' + (isNew ? ' is-new' : '');
      el.dataset.id = String(n.id);
      el.style.left = (n.x || 40) + 'px';
      el.style.top = (n.y || 40) + 'px';
      el.style.width = (n.w || 240) + 'px';
      el.style.zIndex = String(n.z || 1);
      el.style.setProperty('--rot', '0deg');
      const dueClass = (t.due || '').toLowerCase().includes('today') ? 'color:var(--risk);font-weight:700' : 'color:var(--ink-70)';
      el.innerHTML = `
        <div class="ws-note-bar" data-drag="1">
          <div class="ws-note-grip"><span></span><span></span><span></span></div>
          <button class="ws-note-x" title="Remove from board" aria-label="Remove from board" style="color:var(--navy)"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></button>
        </div>
        <div class="ws-card-h"><span style="display:inline-flex;width:14px;height:14px;border:1.3px solid currentColor;border-radius:50%;align-items:center;justify-content:center;font-size:8px"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M2.5 8.5L6 12l7.5-8"/></svg></span></span><span class="src-id">${t.id || ''}</span><span class="h-spacer"></span>${WS_PRIORITY_ICO[t.priority] || ''} <span style="font-size:10px;color:var(--ink-45)">${t.priority || ''}</span></div>
        <div class="ws-card-bd">${pbEsc(t.title || '')}</div>
        <div class="ws-card-foot"><span style="${dueClass}">Due: ${(t.due || '—').replace(/</g, '&lt;')}</span><span>${(t.src || '').replace(/</g, '&lt;')}</span></div>`;
      el.querySelector('.ws-note-x')!.addEventListener('click', () => wsDeleteNote(n.id));
      _wsWireNote(el);
      return el;
    }

    function _wsBuildRisk(n: WSNote, isNew: boolean): HTMLDivElement {
      const r = (n.sourceData || {}) as Record<string, string>;
      const el = document.createElement('div');
      el.className = 'ws-note ws-card kind-risk' + (isNew ? ' is-new' : '');
      el.dataset.id = String(n.id);
      el.style.left = (n.x || 40) + 'px';
      el.style.top = (n.y || 40) + 'px';
      el.style.width = (n.w || 240) + 'px';
      el.style.zIndex = String(n.z || 1);
      el.style.setProperty('--rot', '0deg');
      const trendIco = r.trend === 'up' ? '<span style="color:var(--risk)">▲</span>' : r.trend === 'down' ? '<span style="color:var(--ok)">▼</span>' : '<span style="color:var(--ink-45)">▬</span>';
      const scoreClass = (r.level === 'High') ? 's-h' : (r.level === 'Medium') ? 's-m' : '';
      el.innerHTML = `
        <div class="ws-note-bar" data-drag="1">
          <div class="ws-note-grip"><span></span><span></span><span></span></div>
          <button class="ws-note-x" title="Remove from board" aria-label="Remove from board" style="color:#7F0F1A"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></button>
        </div>
        <div class="ws-card-h"><span style="font-size:13px"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5L15 14H1z"/><path d="M8 6v4"/><circle cx="8" cy="12" r=".6" fill="currentColor"/></svg></span></span><span class="src-id">${r.id || ''}</span><span class="h-spacer"></span><span class="score-pill ${scoreClass}">Score ${r.score || '?'} · ${r.level || '?'}</span></div>
        <div class="ws-card-bd">${pbEsc(r.title || '')}</div>
        <div class="ws-card-foot"><span>Owner: ${pbEsc(r.owner || '—')}</span><span>Trend: ${trendIco}</span></div>`;
      el.querySelector('.ws-note-x')!.addEventListener('click', () => wsDeleteNote(n.id));
      _wsWireNote(el);
      return el;
    }

    function _wsWireNote(el: HTMLDivElement) {
      el.querySelectorAll('[contenteditable="true"]').forEach((field) => {
        field.addEventListener('blur', () => {
          const id = parseInt(el.dataset.id!, 10);
          const n = WS_NOTES.find((x) => x.id === id);
          if (!n) return;
          const f = field as HTMLElement;
          n[f.dataset.field as 'title' | 'body'] = f.textContent || '';
          n.ts = new Date().toISOString();
          const footSpan = el.querySelector('.ws-note-foot span:first-child');
          if (footSpan) footSpan.textContent = _wsFmtDate(n.ts);
          wsSave();
        });
      });
      el.addEventListener('mousedown', (e) => {
        if (WS_CONNECT_MODE) {
          e.preventDefault();
          e.stopPropagation();
          const onUp = () => {
            document.removeEventListener('mouseup', onUp, true);
            wsHandleCardClickInConnectMode(el);
          };
          document.addEventListener('mouseup', onUp, true);
          return;
        }
        const id = parseInt(el.dataset.id!, 10);
        const n = WS_NOTES.find((x) => x.id === id);
        if (!n) return;
        WS_TOP_Z++;
        n.z = WS_TOP_Z;
        el.style.zIndex = String(WS_TOP_Z);
      }, true);
      const bar = el.querySelector('.ws-note-bar') as HTMLElement;
      if (bar) {
        bar.addEventListener('mousedown', (e) => {
          if (WS_CONNECT_MODE) return;
          _wsStartDrag(e, el);
        });
      }
    }

    function _wsStartDrag(e: MouseEvent, el: HTMLDivElement) {
      const target = e.target as HTMLElement;
      if (target.closest('.ws-note-x, .ws-note-color-cycle')) return;
      e.preventDefault();
      const board = document.getElementById('ws-board');
      if (!board) return;
      const rect = el.getBoundingClientRect();
      WS_DRAG = { el, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
      el.classList.add('dragging');
      document.addEventListener('mousemove', _wsDragMove);
      document.addEventListener('mouseup', _wsDragEnd);
    }

    function _wsDragMove(e: MouseEvent) {
      if (!WS_DRAG) return;
      const board = document.getElementById('ws-board');
      if (!board) return;
      const boardRect = board.getBoundingClientRect();
      let x = e.clientX - boardRect.left - WS_DRAG.offsetX;
      let y = e.clientY - boardRect.top - WS_DRAG.offsetY;
      x = Math.max(-20, Math.min(x, board.offsetWidth - 80));
      y = Math.max(0, Math.min(y, board.offsetHeight - 40));
      WS_DRAG.el.style.left = x + 'px';
      WS_DRAG.el.style.top = y + 'px';
      if (_wsConnRaf === null) {
        _wsConnRaf = requestAnimationFrame(() => { _wsConnRaf = null; _wsRenderConnectors(); });
      }
    }

    function _wsDragEnd() {
      if (!WS_DRAG) return;
      const el = WS_DRAG.el;
      el.classList.remove('dragging');
      const id = parseInt(el.dataset.id!, 10);
      const n = WS_NOTES.find((x) => x.id === id);
      if (n) {
        n.x = parseInt(el.style.left, 10) || 0;
        n.y = parseInt(el.style.top, 10) || 0;
        n.z = parseInt(el.style.zIndex, 10) || 1;
        wsSave();
      }
      WS_DRAG = null;
      document.removeEventListener('mousemove', _wsDragMove);
      document.removeEventListener('mouseup', _wsDragEnd);
      if (_wsConnRaf !== null) { cancelAnimationFrame(_wsConnRaf); _wsConnRaf = null; }
      _wsRenderConnectors();
    }

    function wsRender() {
      const board = document.getElementById('ws-board');
      if (!board) return;
      const empty = document.getElementById('ws-empty');
      board.querySelectorAll('.ws-note').forEach((el) => el.remove());
      if (empty) (empty as HTMLElement).style.display = WS_NOTES.length === 0 ? 'block' : 'none';
      WS_NOTES.forEach((n) => board.appendChild(_wsBuildNote(n, false)));
      _wsRenderConnectors();
      _wsUpdateCounters();
    }

    // ── connectors ──
    function wsToggleConnectMode() {
      WS_CONNECT_MODE = !WS_CONNECT_MODE;
      WS_CONN_SOURCE = null;
      document.querySelectorAll('.ws-note').forEach((el) => el.classList.remove('ws-conn-source'));
      document.body.classList.toggle('ws-connect-mode', WS_CONNECT_MODE);
      const btn = document.getElementById('ws-connect-btn');
      if (btn) {
        btn.classList.toggle('active', WS_CONNECT_MODE);
        btn.innerHTML = WS_CONNECT_MODE
          ? '<span style="font-weight:700;font-size:13px"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></span>&nbsp;Done connecting'
          : '<span style="font-weight:700;font-size:13px">⇄</span>&nbsp;Connect';
      }
    }

    function wsHandleCardClickInConnectMode(cardEl: HTMLDivElement): boolean {
      if (!WS_CONNECT_MODE) return false;
      const id = parseInt(cardEl.dataset.id!, 10);
      if (WS_CONN_SOURCE === null) { WS_CONN_SOURCE = id; cardEl.classList.add('ws-conn-source'); return true; }
      if (WS_CONN_SOURCE === id) { cardEl.classList.remove('ws-conn-source'); WS_CONN_SOURCE = null; return true; }
      const exists = WS_CONNECTORS.find((c) => (c.fromId === WS_CONN_SOURCE && c.toId === id) || (c.fromId === id && c.toId === WS_CONN_SOURCE));
      if (!exists) {
        WS_CONNECTORS.push({ id: WS_CONN_NEXT_ID++, fromId: WS_CONN_SOURCE, toId: id, kind: 'leads-to' });
      }
      document.querySelectorAll('.ws-note.ws-conn-source').forEach((el) => el.classList.remove('ws-conn-source'));
      WS_CONN_SOURCE = null;
      _wsRenderConnectors();
      wsSave();
      return true;
    }

    function wsDeleteConnector(connId: number) {
      const idx = WS_CONNECTORS.findIndex((c) => c.id === connId);
      if (idx < 0) return;
      WS_CONNECTORS.splice(idx, 1);
      _wsRenderConnectors();
      wsSave();
    }

    function wsCycleConnectorKind(connId: number) {
      const c = WS_CONNECTORS.find((x) => x.id === connId);
      if (!c) return;
      const cur = c.kind || 'leads-to';
      const idx = WS_CONN_KINDS.indexOf(cur);
      c.kind = WS_CONN_KINDS[(idx + 1) % WS_CONN_KINDS.length];
      _wsRenderConnectors();
      wsSave();
    }

    function _wsCardAttachPoint(cardEl: HTMLDivElement, otherX: number, otherY: number) {
      const x = parseInt(cardEl.style.left, 10) || 0;
      const y = parseInt(cardEl.style.top, 10) || 0;
      const w = cardEl.offsetWidth || 240;
      const h = cardEl.offsetHeight || 140;
      const cx = x + w / 2, cy = y + h / 2;
      const dx = otherX - cx, dy = otherY - cy;
      if (Math.abs(dx) * h > Math.abs(dy) * w) { return dx > 0 ? { x: x + w, y: cy } : { x, y: cy }; }
      return dy > 0 ? { x: cx, y: y + h } : { x: cx, y };
    }

    function _wsHighlightedConnectorIds(): Set<number> {
      const empty = new Set<number>();
      if (!WS_HIGHLIGHTED_STEP) return empty;
      const plan = lastPlanRef.current;
      if (!plan || !plan.steps) return empty;
      const step = plan.steps[WS_HIGHLIGHTED_STEP - 1];
      if (!step) return empty;
      const refs = _wsParseStepRefs(step);
      if (refs.length < 2) return empty;
      const refIds = new Set(refs);
      const out = new Set<number>();
      WS_CONNECTORS.forEach((c) => {
        const fromNote = WS_NOTES.find((n) => n.id === c.fromId);
        const toNote = WS_NOTES.find((n) => n.id === c.toId);
        if (!fromNote || !toNote) return;
        const fromMatches = fromNote.sourceId && refIds.has(fromNote.sourceId);
        const toMatches = toNote.sourceId && refIds.has(toNote.sourceId);
        if (fromMatches && toMatches) out.add(c.id);
      });
      return out;
    }

    function _wsRenderConnectors() {
      const svg = document.getElementById('ws-connectors-svg');
      if (!svg) return;
      svg.querySelectorAll('g.ws-conn').forEach((g) => g.remove());
      const board = document.getElementById('ws-board');
      if (board) board.querySelectorAll('.ws-conn-label').forEach((el) => el.remove());
      const highlightedIds = _wsHighlightedConnectorIds();
      for (const conn of WS_CONNECTORS) {
        const fromCard = document.querySelector('.ws-note[data-id="' + conn.fromId + '"]') as HTMLDivElement;
        const toCard = document.querySelector('.ws-note[data-id="' + conn.toId + '"]') as HTMLDivElement;
        if (!fromCard || !toCard) continue;
        const kind = conn.kind || 'leads-to';
        const fx0 = parseInt(fromCard.style.left, 10) + fromCard.offsetWidth / 2;
        const fy0 = parseInt(fromCard.style.top, 10) + fromCard.offsetHeight / 2;
        const tx0 = parseInt(toCard.style.left, 10) + toCard.offsetWidth / 2;
        const ty0 = parseInt(toCard.style.top, 10) + toCard.offsetHeight / 2;
        const A = _wsCardAttachPoint(fromCard, tx0, ty0);
        const B = _wsCardAttachPoint(toCard, fx0, fy0);
        const dx = (B.x - A.x), dy = (B.y - A.y);
        const isHorizontal = Math.abs(dx) > Math.abs(dy);
        let d: string;
        if (isHorizontal) {
          const ctrlOffset = Math.max(40, Math.abs(dx) * 0.45);
          d = `M ${A.x} ${A.y} C ${A.x + (dx > 0 ? ctrlOffset : -ctrlOffset)} ${A.y}, ${B.x - (dx > 0 ? ctrlOffset : -ctrlOffset)} ${B.y}, ${B.x} ${B.y}`;
        } else {
          const co = Math.max(40, Math.abs(dy) * 0.45);
          d = `M ${A.x} ${A.y} C ${A.x} ${A.y + (dy > 0 ? co : -co)}, ${B.x} ${B.y - (dy > 0 ? co : -co)}, ${B.x} ${B.y}`;
        }
        const NS = 'http://www.w3.org/2000/svg';
        const g = document.createElementNS(NS, 'g');
        g.setAttribute('class', 'ws-conn');
        g.dataset.id = String(conn.id);
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', d);
        path.setAttribute('fill', 'none');
        path.setAttribute('class', 'ws-conn-path kind-' + kind + (highlightedIds.has(conn.id) ? ' ws-conn-highlight' : ''));
        if (kind !== 'maps-to') {
          const arrowMarker = highlightedIds.has(conn.id) ? 'url(#ws-arrow-gold)' : (kind === 'blocks' ? 'url(#ws-arrow-red)' : 'url(#ws-arrow)');
          path.setAttribute('marker-end', arrowMarker);
        }
        g.appendChild(path);
        const hit = document.createElementNS(NS, 'path');
        hit.setAttribute('d', d);
        hit.setAttribute('class', 'ws-conn-hit');
        g.appendChild(hit);
        svg.appendChild(g);
        if (board) {
          const midX = (A.x + B.x) / 2, midY = (A.y + B.y) / 2;
          const label = document.createElement('div');
          label.className = 'ws-conn-label kind-' + kind;
          label.style.left = midX + 'px';
          label.style.top = midY + 'px';
          label.dataset.connId = String(conn.id);
          label.title = 'Click to cycle kind · hover and click ✕ to delete';
          label.innerHTML = WS_CONN_LABELS[kind] + '<span class="conn-x" title="Delete connector"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></span>';
          label.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).classList.contains('conn-x')) { e.stopPropagation(); wsDeleteConnector(conn.id); return; }
            e.stopPropagation();
            wsCycleConnectorKind(conn.id);
          });
          board.appendChild(label);
        }
      }
    }

    // ── note CRUD ──
    function wsAddNote() {
      const offset = (WS_NOTES.length % 12) * 26;
      const n: WSNote = { id: WS_NEXT_ID++, kind: 'note', title: '', body: '', color: WS_SELECTED_COLOR, x: 40 + offset, y: 40 + offset, w: 280, h: 240, z: ++WS_TOP_Z, ts: new Date().toISOString() };
      WS_NOTES.push(n);
      const empty = document.getElementById('ws-empty');
      if (empty) (empty as HTMLElement).style.display = 'none';
      const board = document.getElementById('ws-board');
      const el = _wsBuildNote(n, true);
      board!.appendChild(el);
      setTimeout(() => { const body = el.querySelector('[data-field="body"]') as HTMLElement; if (body) body.focus(); }, 80);
      _wsUpdateCounters();
      wsSave();
    }

    function wsDeleteNote(id: number) {
      const idx = WS_NOTES.findIndex((n) => n.id === id);
      if (idx < 0) return;
      WS_NOTES.splice(idx, 1);
      WS_CONNECTORS = WS_CONNECTORS.filter((c) => c.fromId !== id && c.toId !== id);
      const el = document.querySelector('.ws-note[data-id="' + id + '"]') as HTMLElement;
      if (el) {
        el.style.transition = 'all .2s';
        el.style.opacity = '0';
        el.style.transform = 'scale(0.85) rotate(-3deg)';
        setTimeout(() => el.remove(), 200);
      }
      if (WS_NOTES.length === 0) { const empty = document.getElementById('ws-empty'); if (empty) (empty as HTMLElement).style.display = 'block'; }
      _wsUpdateCounters();
      _wsRenderConnectors();
      wsSave();
    }

    function wsCycleColor(id: number) {
      const n = WS_NOTES.find((x) => x.id === id);
      if (!n) return;
      const idx = WS_COLORS.indexOf(n.color || 'yellow');
      n.color = WS_COLORS[(idx + 1) % WS_COLORS.length];
      const el = document.querySelector('.ws-note[data-id="' + id + '"]') as HTMLElement;
      if (el) {
        WS_COLORS.forEach((c) => el.classList.remove('c-' + c));
        el.classList.add('c-' + n.color);
        const cycle = el.querySelector('.ws-note-color-cycle');
        if (cycle) cycle.textContent = String(WS_COLORS.indexOf(n.color) + 1);
      }
      wsSave();
    }

    function wsSelectColor(color: string, swatchEl: HTMLElement) {
      WS_SELECTED_COLOR = color;
      document.querySelectorAll('.ws-swatch').forEach((s) => s.classList.remove('selected'));
      if (swatchEl) swatchEl.classList.add('selected');
    }

    function wsClearAll() {
      if (WS_NOTES.length === 0 && WS_CONNECTORS.length === 0) return;
      if (!window.confirm('Clear all ' + WS_NOTES.length + ' item(s) and ' + WS_CONNECTORS.length + ' connector(s) from your workspace? This cannot be undone.')) return;
      WS_NOTES = []; WS_CONNECTORS = []; WS_NEXT_ID = 1; WS_TOP_Z = 1; WS_CONN_NEXT_ID = 1;
      wsDismissPlanStrip();
      wsRender();
      wsSave();
    }

    // ── pickers ──
    function wsOpenPicker(kind: string) {
      const cfg = WS_PICKER_LABELS[kind];
      if (!cfg) return;
      const sources: Array<Record<string, unknown>> = { jira: dsJira(), task: dsTasks(), risk: dsRisks() }[kind] as Array<Record<string, unknown>>;
      const onBoardIds = new Set(WS_NOTES.filter((n) => n.kind === kind).map((n) => n.sourceId));
      const titleHtml = '<span style="display:inline-flex;align-items:center;gap:9px">' + cfg.icon + ' ' + cfg.title + '</span>';
      let rowsHtml = '';
      for (const item of sources) {
        const isOnBoard = onBoardIds.has(item.id as string);
        if (kind === 'jira') {
          rowsHtml += `<div class="ws-picker-row${isOnBoard ? ' added' : ''}" data-search="${((item.id as string) + ' ' + (item.summary as string)).toLowerCase().replace(/"/g, '&quot;')}" data-kind="jira" data-id="${item.id}">
            <span class="pr-id">${item.id}</span><span class="pr-summary">${pbEsc(item.summary as string)}</span>
            <span class="pr-meta">${WS_PRIORITY_ICO[item.priority as string] || ''} ${item.status}</span></div>`;
        } else if (kind === 'task') {
          rowsHtml += `<div class="ws-picker-row${isOnBoard ? ' added' : ''}" data-search="${((item.id as string) + ' ' + (item.title as string)).toLowerCase().replace(/"/g, '&quot;')}" data-kind="task" data-id="${item.id}">
            <span class="pr-id">${item.id}</span><span class="pr-summary">${pbEsc(item.title as string)}</span>
            <span class="pr-meta">${item.due} · ${WS_PRIORITY_ICO[item.priority as string] || ''}</span></div>`;
        } else {
          const scoreClass = item.level === 'High' ? 's-h' : item.level === 'Medium' ? 's-m' : '';
          rowsHtml += `<div class="ws-picker-row${isOnBoard ? ' added' : ''}" data-search="${((item.id as string) + ' ' + (item.title as string)).toLowerCase().replace(/"/g, '&quot;')}" data-kind="risk" data-id="${item.id}">
            <span class="pr-id">${item.id}</span><span class="pr-summary">${pbEsc(item.title as string)}</span>
            <span class="pr-meta"><span class="score-pill ${scoreClass}">${item.score}·${(item.level as string)[0]}</span> · ${pbEsc(item.owner as string)}</span></div>`;
        }
      }
      const bodyHtml = `
        <div style="display:flex;gap:8px;margin-bottom:10px">
          <input type="text" class="ws-picker-search" id="ws-picker-search" placeholder="Search by ID or keyword…" autofocus style="flex:1;margin:0">
        </div>
        <div class="ws-picker-list" id="ws-picker-list">${rowsHtml || '<div style="padding:20px;text-align:center;color:var(--ink-45);font-size:12px">No items available. Data sources will populate when connected to Atlas backend.</div>'}</div>
        <div style="margin-top:9px;font-size:10.5px;color:var(--ink-45);font-family:var(--mono)">Click any row to add an existing item to your workspace board.</div>`;
      const footerHtml = '<button class="btn btn--sm" onclick="document.querySelector(\'.ws-modal-overlay\')?.remove()">Close</button>';
      openMo(titleHtml, bodyHtml, footerHtml, 'lg');
      setTimeout(() => {
        const inp = document.getElementById('ws-picker-search');
        if (inp) {
          inp.focus();
          inp.addEventListener('input', (e) => {
            const q = (e.target as HTMLInputElement).value.toLowerCase();
            document.querySelectorAll('#ws-picker-list .ws-picker-row').forEach((r) => {
              (r as HTMLElement).style.display = (r.dataset.search || '').includes(q) ? '' : 'none';
            });
          });
        }
      }, 80);
    }

    function _wsPlaceCard(kind: string, source: Record<string, unknown>, focus: boolean): WSNote | null {
      if (!source) return null;
      if (WS_NOTES.find((n) => n.kind === kind && n.sourceId === source.id)) return null;
      const offset = (WS_NOTES.length % 12) * 26;
      const item: WSNote = { id: WS_NEXT_ID++, kind: kind as WSNote['kind'], sourceId: source.id as string, sourceData: { ...source }, x: 40 + offset, y: 40 + offset, w: 240, z: ++WS_TOP_Z, ts: new Date().toISOString() };
      WS_NOTES.push(item);
      const board = document.getElementById('ws-board');
      if (board) {
        const el = _wsBuildNote(item, true);
        board.appendChild(el);
        const empty = document.getElementById('ws-empty'); if (empty) (empty as HTMLElement).style.display = 'none';
        if (focus) setTimeout(() => {
          try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch { /* noop */ }
          el.classList.remove('ws-pulse'); void el.offsetWidth; el.classList.add('ws-pulse');
          setTimeout(() => el.classList.remove('ws-pulse'), 2500);
        }, 60);
      }
      _wsUpdateCounters();
      wsSave();
      return item;
    }

    function wsQuickCreate(kind: string) {
      const titleLabel = kind === 'task' ? 'Task title' : 'Risk title';
      const extra = kind === 'task'
        ? `<div class="jt-row"><label class="jt-label">Priority</label><select id="wsqc-prio" class="jt-input"><option>Highest</option><option selected>High</option><option>Medium</option><option>Low</option></select></div>
           <div class="jt-row"><label class="jt-label">Due</label><input id="wsqc-due" class="jt-input" type="text" value="This week" placeholder="e.g. Fri COB, 16 May"></div>`
        : `<div class="jt-row"><label class="jt-label">Severity</label><select id="wsqc-level" class="jt-input"><option>High</option><option selected>Medium</option><option>Low</option></select></div>
           <div class="jt-row"><label class="jt-label">Owner</label><input id="wsqc-owner" class="jt-input" type="text" value="Admin User" placeholder="Risk owner"></div>`;
      const titleHtml = '<span style="display:inline-flex;align-items:center;gap:9px">Create new ' + kind + '</span>';
      const bodyHtml = `
        <div class="jt-form">
          <div class="jt-row"><label class="jt-label">${titleLabel}<span class="req">*</span></label>
            <input id="wsqc-title" class="jt-input" type="text" placeholder="${kind === 'task' ? 'What needs to be done?' : 'What could go wrong?'}" autofocus></div>
          ${extra}
        </div>`;
      const footerHtml = '<button class="btn btn--sm" onclick="document.querySelector(\'.ws-modal-overlay\')?.remove()">Cancel</button>'
        + `<button class="btn btn--primary btn--sm" id="wsqc-submit">Create ${kind}</button>`;
      openMo(titleHtml, bodyHtml, footerHtml, 'lg');
      setTimeout(() => {
        const t = document.getElementById('wsqc-title'); if (t) t.focus();
        const submitBtn = document.getElementById('wsqc-submit');
        if (submitBtn) submitBtn.addEventListener('click', () => wsQuickCreateSubmit(kind));
      }, 70);
    }

    function wsQuickCreateSubmit(kind: string) {
      const titleEl = document.getElementById('wsqc-title') as HTMLInputElement;
      const title = (titleEl?.value || '').trim();
      if (!title) { window.alert('Title is required.'); return; }
      let source: Record<string, unknown>;
      if (kind === 'task') {
        const id = 'TK-NEW-' + (WS_QC_COUNTER++);
        const priority = (document.getElementById('wsqc-prio') as HTMLSelectElement)?.value || 'High';
        const due = ((document.getElementById('wsqc-due') as HTMLInputElement)?.value || 'This week').trim();
        source = { id, title, due, priority, src: 'Created on workspace' };
      } else {
        const id = 'R-NEW-' + (WS_QC_COUNTER++);
        const level = (document.getElementById('wsqc-level') as HTMLSelectElement)?.value || 'Medium';
        const owner = ((document.getElementById('wsqc-owner') as HTMLInputElement)?.value || 'Admin User').trim();
        const score = level === 'High' ? 12 : level === 'Medium' ? 9 : 4;
        source = { id, title, score, level, trend: 'flat', owner, category: 'Created on workspace' };
      }
      closeMo();
      setTimeout(() => _wsPlaceCard(kind, source, true), 120);
    }

    // ── demo scenario ──
    function wsLoadDemoScenario() {
      if (WS_NOTES.length > 0) {
        if (!window.confirm('Replace the current ' + WS_NOTES.length + ' item(s) with the demo scenario?')) return;
      }
      WS_NOTES = []; WS_CONNECTORS = []; WS_NEXT_ID = 1; WS_TOP_Z = 1; WS_CONN_NEXT_ID = 1;
      wsDismissPlanStrip();
      const indexToId: number[] = [];
      for (const tpl of WS_DEMO_SCENARIO) {
        const item: WSNote = { id: WS_NEXT_ID++, kind: tpl.kind as WSNote['kind'], x: tpl.x as number, y: tpl.y as number, w: 240, z: ++WS_TOP_Z, ts: new Date().toISOString() };
        if (tpl.kind === 'note') { item.color = tpl.color as string; item.title = tpl.title as string; item.body = tpl.body as string; }
        else {
          item.sourceId = tpl.sourceId as string;
          const sources: Record<string, Array<Record<string, unknown>>> = { jira: dsJira(), task: dsTasks(), risk: dsRisks() };
          const src = sources[tpl.kind as string]?.find((x) => x.id === tpl.sourceId);
          if (src) item.sourceData = { ...src };
        }
        WS_NOTES.push(item);
        indexToId.push(item.id);
      }
      for (const c of WS_DEMO_CONNECTORS) {
        WS_CONNECTORS.push({ id: WS_CONN_NEXT_ID++, fromId: indexToId[c.from], toId: indexToId[c.to], kind: c.kind });
      }
      wsRender();
      wsSave();
    }

    // ── orchestrator ──
    function _wsOrchBuildPrompt(): string {
      const notes = WS_NOTES.filter((n) => n.kind === 'note' || !n.kind);
      const jira = WS_NOTES.filter((n) => n.kind === 'jira');
      const tasks = WS_NOTES.filter((n) => n.kind === 'task');
      const risks = WS_NOTES.filter((n) => n.kind === 'risk');
      let prompt = 'Here are the items currently on my workspace board:\n\n';
      if (notes.length) {
        prompt += `NOTES (${notes.length}):\n`;
        notes.forEach((n) => {
          const t = (n.title || '').trim(); const b = (n.body || '').trim();
          const line = t ? (t + (b ? ' — ' + b : '')) : b;
          if (line) prompt += `- ${line}\n`;
        });
        prompt += '\n';
      }
      if (jira.length) {
        prompt += `JIRA TICKETS (${jira.length}):\n`;
        jira.forEach((n) => { const t = (n.sourceData || {}) as Record<string, string>; prompt += `- ${t.id}: ${t.summary} (${t.type}, ${t.priority}, ${t.status})\n`; });
        prompt += '\n';
      }
      if (tasks.length) {
        prompt += `TASKS (${tasks.length}):\n`;
        tasks.forEach((n) => { const t = (n.sourceData || {}) as Record<string, string>; prompt += `- ${t.id}: ${t.title} (Due ${t.due}, ${t.priority})\n`; });
        prompt += '\n';
      }
      if (risks.length) {
        prompt += `RISKS (${risks.length}):\n`;
        risks.forEach((n) => { const r = (n.sourceData || {}) as Record<string, string>; prompt += `- ${r.id}: ${r.title} (Score ${r.score} ${r.level}, owner ${r.owner})\n`; });
        prompt += '\n';
      }
      prompt += 'Produce a course-of-action plan to sequence and resolve these items.';
      return prompt;
    }

    function _wsOrchFallbackPlan(): OrchPlan {
      return {
        problem: 'Workspace contains ' + WS_NOTES.length + ' items spanning notes, Jira, tasks, and risks. The Orchestrator outlines a generic triage approach.',
        steps: [
          { num: '1', verb: 'TRIAGE', what: 'Sort the highest-severity items first: any risk scoring 12+ or ticket marked Highest priority gets an action owner today.', who: 'Admin', when: 'Today, COB' },
          { num: '2', verb: 'BATCH', what: 'Group remaining items by owner and dependency. Forward to assignees with 48-hour first-touch expectations.', who: 'Admin', when: '48 hours' },
          { num: '3', verb: 'TRACK', what: 'Stand up a daily standup or async check-in cadence for items with downstream blocking effects.', who: 'Admin · team leads', when: 'Daily, starting tomorrow' },
          { num: '4', verb: 'REVIEW', what: 'Friday afternoon close-out: confirm what moved, what slipped, what to escalate.', who: 'Admin', when: 'Fri, 1500 ET' },
        ],
        outcome: 'Workspace items either closed, in flight with named owners, or formally escalated by end of week — no orphaned items.',
      };
    }

    async function wsOrchestrate() {
      if (WS_NOTES.length === 0) {
        if (window.confirm('Your workspace is empty. Load the demo scenario to see how the Orchestrator works?')) {
          wsLoadDemoScenario();
          setTimeout(() => wsOrchestrate(), 600);
        }
        return;
      }
      const titleHtml = '<span style="display:inline-flex;align-items:center;gap:9px"><span style="font-size:16px;color:var(--gold)"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5l1.7 4.8L14.5 8l-4.8 1.7L8 14.5l-1.7-4.8L1.5 8l4.8-1.7z" fill="currentColor"/></svg></span></span>Orchestrator · Recommended Course of Action</span>';
      const noteCount = WS_NOTES.filter((n) => n.kind === 'note' || !n.kind).length;
      const jiraCount = WS_NOTES.filter((n) => n.kind === 'jira').length;
      const taskCount = WS_NOTES.filter((n) => n.kind === 'task').length;
      const riskCount = WS_NOTES.filter((n) => n.kind === 'risk').length;
      const loadingBody = `
        <div class="orch-context-summary">
          <span><strong>${noteCount}</strong> notes</span>
          <span><strong>${jiraCount}</strong> Jira</span>
          <span><strong>${taskCount}</strong> tasks</span>
          <span><strong>${riskCount}</strong> risks</span>
          <span style="margin-left:auto;color:var(--gold);font-weight:700">Reading workspace…</span>
        </div>
        <div class="orch-loading">
          <div class="spinner"></div>
          <div class="orch-loading-text">Analyzing ${WS_NOTES.length} workspace item${WS_NOTES.length === 1 ? '' : 's'}…</div>
          <div class="orch-loading-sub">ATLAS ORCHESTRATOR · PROMPT WRAPPED IN SCHEMA</div>
        </div>`;
      openMo(titleHtml, loadingBody, '<button class="btn btn--sm" id="orch-cancel-btn">Cancel</button>', 'xl');
      setTimeout(() => {
        const cancelBtn = document.getElementById('orch-cancel-btn');
        if (cancelBtn) cancelBtn.addEventListener('click', closeMo);
      }, 50);

      let plan: OrchPlan | null = null;
      // Use fallback plan (no external AI model in Atlas)
      await new Promise(resolve => setTimeout(resolve, 800)); // simulate processing
      plan = _wsOrchFallbackPlan();
      _wsOrchRender(plan, { noteCount, jiraCount, taskCount, riskCount }, true, titleHtml);
    }

    function _wsOrchRender(plan: OrchPlan, counts: { noteCount: number; jiraCount: number; taskCount: number; riskCount: number }, usedFallback: boolean, titleHtml: string) {
      const stepsHtml = plan.steps.map((s) => `
        <li class="orch-step">
          <div class="orch-step-num">${s.num || '·'}</div>
          <div class="orch-step-l">
            ${s.verb ? '<div class="orch-step-verb">' + s.verb.replace(/</g, '&lt;') + '</div>' : ''}
            <div class="orch-step-what">${(s.what || '').replace(/</g, '&lt;')}</div>
            <div class="orch-step-meta">
              ${s.who ? '<span><span class="meta-k">WHO:</span> ' + s.who.replace(/</g, '&lt;') + '</span>' : ''}
              ${s.when ? '<span><span class="meta-k">WHEN:</span> ' + s.when.replace(/</g, '&lt;') + '</span>' : ''}
            </div>
          </div>
        </li>`).join('');
      const bodyHtml = `
        <div class="orch-banner">
          <div class="orch-banner-ico"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5l1.7 4.8L14.5 8l-4.8 1.7L8 14.5l-1.7-4.8L1.5 8l4.8-1.7z" fill="currentColor"/></svg></span></div>
          <div class="orch-banner-l">
            <div class="orch-banner-t">${plan.steps.length}-step course of action</div>
            <div class="orch-banner-s">Synthesized from ${counts.noteCount} note${counts.noteCount === 1 ? '' : 's'} · ${counts.jiraCount} Jira · ${counts.taskCount} task${counts.taskCount === 1 ? '' : 's'} · ${counts.riskCount} risk${counts.riskCount === 1 ? '' : 's'} ${usedFallback ? '· deterministic offline plan' : '· live synthesis'}</div>
          </div>
        </div>
        <div class="orch-result">
          <div class="orch-section">
            <div class="orch-label">Problem framing</div>
            <div class="orch-text">${(plan.problem || '').replace(/</g, '&lt;')}</div>
          </div>
          <div class="orch-section">
            <div class="orch-label">Recommended sequence</div>
            <ol class="orch-steps">${stepsHtml}</ol>
          </div>
          <div class="orch-section">
            <div class="orch-label">Desired outcome</div>
            <div class="orch-text" style="border-left-color:var(--ok);background:var(--ok-wash)">${(plan.outcome || '').replace(/</g, '&lt;')}</div>
          </div>
        </div>`;
      const footerHtml = '<button class="btn btn--sm" id="orch-close-btn">Close</button>'
        + '<button class="btn btn--sm" id="orch-copy-btn"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3.5" y="3" width="9" height="11.5" rx="1"/><rect x="5.5" y="1.5" width="5" height="2.5" rx=".5"/><path d="M6 7h4M6 9.5h4M6 12h3"/></svg></span> Copy plan</button>'
        + '<button class="btn btn--sm" id="orch-save-note-btn">＋ Save as note</button>'
        + '<button class="btn btn--primary btn--sm" id="orch-highlight-btn"><span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M8 1.5l1.7 4.8L14.5 8l-4.8 1.7L8 14.5l-1.7-4.8L1.5 8l4.8-1.7z" fill="currentColor"/></svg></span> Highlight on board</button>';
      openMo(titleHtml, bodyHtml, footerHtml, 'xl');
      lastPlanRef.current = plan;
      setTimeout(() => {
        document.getElementById('orch-close-btn')?.addEventListener('click', closeMo);
        document.getElementById('orch-copy-btn')?.addEventListener('click', () => _wsOrchCopyToClipboard());
        document.getElementById('orch-save-note-btn')?.addEventListener('click', () => _wsOrchSaveAsNote());
        document.getElementById('orch-highlight-btn')?.addEventListener('click', () => wsShowPlanStripFromModal());
      }, 50);
    }

    function _wsOrchCopyToClipboard() {
      const p = lastPlanRef.current;
      if (!p) return;
      let text = 'COURSE OF ACTION\n\nPROBLEM\n' + p.problem + '\n\nPLAN\n';
      p.steps.forEach((s) => {
        text += `${s.num}. ${s.verb} — ${s.what}\n`;
        if (s.who) text += `   WHO: ${s.who}\n`;
        if (s.when) text += `   WHEN: ${s.when}\n`;
        text += '\n';
      });
      text += 'OUTCOME\n' + p.outcome + '\n';
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => showToast('Plan copied to clipboard', 'ok'));
      }
    }

    function _wsOrchSaveAsNote() {
      const p = lastPlanRef.current;
      if (!p) return;
      let body = 'Problem: ' + p.problem + '\n\n';
      p.steps.forEach((s) => { body += `${s.num}. ${s.verb} — ${s.what}\n   ${s.who ? '· ' + s.who : ''} ${s.when ? '· ' + s.when : ''}\n\n`; });
      body += 'Outcome: ' + p.outcome;
      const maxX = WS_NOTES.reduce((m, n) => Math.max(m, (n.x || 0) + (n.w || 230)), 0);
      const item: WSNote = {
        id: WS_NEXT_ID++, kind: 'note', color: 'green', x: Math.min(maxX + 18, 1080), y: 40, w: 280, z: ++WS_TOP_Z,
        title: 'Plan · ' + p.steps.length + ' steps',
        body, ts: new Date().toISOString(),
      };
      WS_NOTES.push(item);
      const board = document.getElementById('ws-board');
      if (board) {
        const el = _wsBuildNote(item, true);
        board.appendChild(el);
        const empty = document.getElementById('ws-empty'); if (empty) (empty as HTMLElement).style.display = 'none';
      }
      _wsUpdateCounters();
      wsSave();
      closeMo();
    }

    // ── plan strip ──
    function wsShowPlanStripFromModal() {
      closeMo();
      setTimeout(() => wsRenderPlanStrip(), 60);
    }

    function wsRenderPlanStrip() {
      const plan = lastPlanRef.current;
      if (!plan) return;
      const strip = document.getElementById('ws-plan-strip');
      const titleEl = document.getElementById('ws-ps-title');
      const stepsEl = document.getElementById('ws-ps-steps');
      if (!strip || !stepsEl) return;
      if (titleEl) titleEl.textContent = plan.steps.length + '-step course of action';
      let html = '';
      plan.steps.forEach((s, i) => {
        const refs = _wsParseStepRefs(s);
        const refsBadge = refs.length ? '<span class="ps-refs" title="References: ' + refs.join(', ') + '">' + refs.length + ' ref' + (refs.length === 1 ? '' : 's') + '</span>' : '';
        const what = (s.what || '').length > 60 ? s.what.substring(0, 58).replace(/</g, '&lt;') + '…' : (s.what || '').replace(/</g, '&lt;');
        html += `<div class="ps-step" data-step="${i + 1}">
          <div class="ps-num">${s.num || (i + 1)}</div>
          <div class="ps-l">
            <div class="ps-verb">${(s.verb || '').replace(/</g, '&lt;')}</div>
            <div class="ps-what">${what}</div>
          </div>
          ${refsBadge}
        </div>`;
      });
      stepsEl.innerHTML = html;
      // Wire step interactions
      stepsEl.querySelectorAll('.ps-step').forEach((el) => {
        el.addEventListener('mouseenter', () => _wsHighlightStep(parseInt((el as HTMLElement).dataset.step!, 10)));
        el.addEventListener('mouseleave', () => _wsClearHighlight());
        el.addEventListener('click', () => _wsClickStep(parseInt((el as HTMLElement).dataset.step!, 10)));
      });
      _wsTlResetState(plan);
      _wsRenderTimeline(plan);
      wsSwitchPlanView('steps');
      strip.classList.add('show');
    }

    function wsSwitchPlanView(view: string) {
      const stepsEl = document.getElementById('ws-ps-steps');
      const tlEl = document.getElementById('ws-tl-view');
      const hint = document.getElementById('ws-ps-hint');
      document.querySelectorAll('#ws-ps-toggle button').forEach((b) => b.classList.toggle('active', b.getAttribute('data-view') === view));
      if (view === 'timeline') {
        if (stepsEl) stepsEl.classList.add('tl-active');
        if (tlEl) tlEl.classList.add('show');
        if (hint) hint.textContent = 'Steps positioned by their WHEN dates · today marker in gold · hover a bar to highlight';
      } else {
        if (stepsEl) stepsEl.classList.remove('tl-active');
        if (tlEl) tlEl.classList.remove('show');
        if (hint) hint.textContent = 'Hover a step → cards light up · click → details';
      }
    }

    function _wsTlResetState(plan: OrchPlan) {
      WS_TL_STATE.laneMode = WS_TL_STATE.laneMode || 'stage';
      WS_TL_STATE.steps = (plan?.steps || []).map(() => ({ dayOffset: 0, duration: 1 }));
      if (WS_TL_STATE.nowTimer) { clearInterval(WS_TL_STATE.nowTimer); WS_TL_STATE.nowTimer = null; }
    }

    function wsSetLaneMode(mode: string) {
      WS_TL_STATE.laneMode = mode;
      document.querySelectorAll('#ws-tl-controls-bar [data-lane]').forEach((b) => b.classList.toggle('active', (b as HTMLElement).dataset.lane === mode));
      _wsRenderTimeline(lastPlanRef.current);
    }

    function _wsRenderTimeline(plan: OrchPlan | null) {
      const tlEl = document.getElementById('ws-tl-view');
      if (!tlEl || !plan) return;
      if (!WS_TL_STATE.steps || WS_TL_STATE.steps.length !== plan.steps.length) {
        WS_TL_STATE.steps = plan.steps.map(() => ({ dayOffset: 0, duration: 1 }));
      }
      const laneMode = WS_TL_STATE.laneMode || 'stage';
      const stepDates = plan.steps.map((s) => _wsParseWhenDate(s.when));
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const effectiveDates = stepDates.map((sd, i) => {
        const offset = (WS_TL_STATE.steps[i] && WS_TL_STATE.steps[i].dayOffset) || 0;
        if (!sd || !sd.date) return null;
        const d = new Date(sd.date); d.setDate(d.getDate() + offset);
        return d;
      });
      let earliest = today, latest = today;
      effectiveDates.forEach((d) => { if (d) { if (d < earliest) earliest = d; if (d > latest) latest = d; } });
      const minLatest = new Date(today); minLatest.setDate(minLatest.getDate() + 14);
      if (latest < minLatest) latest = minLatest;
      const start = new Date(earliest); start.setDate(start.getDate() - 1);
      const totalDays = Math.round((latest.getTime() - start.getTime()) / 86400000) + 1;
      const dayWidth = 56;
      const labelWidth = 165;
      const legendItems = WS_STAGE_ORDER.map((k) => `<span class="leg-item"><span class="leg-dot" style="background:${WS_STAGES[k].color}"></span>${WS_STAGES[k].label}</span>`).join(' ');
      const controlsHtml = `<div class="ws-tl-controls" id="ws-tl-controls-bar">
        <div class="ws-tl-controls-l">
          <span class="ws-tl-ctrl-label">Group by</span>
          <div class="ws-tl-lane-btns">
            <button class="${laneMode === 'stage' ? 'active' : ''}" data-lane="stage">By Stage</button>
            <button class="${laneMode === 'owner' ? 'active' : ''}" data-lane="owner">By Owner</button>
            <button class="${laneMode === 'flat' ? 'active' : ''}" data-lane="flat">Flat</button>
          </div>
        </div>
        <div class="ws-tl-controls-r">
          <div class="ws-tl-legend">${laneMode === 'owner' ? '' : legendItems}</div>
        </div>
      </div>`;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      let axisDays = '';
      let todayOffset = -1;
      for (let i = 0; i < totalDays; i++) {
        const d = new Date(start); d.setDate(d.getDate() + i);
        const isToday = d.getTime() === today.getTime();
        const dow = d.getDay();
        const isWeekend = dow === 0 || dow === 6;
        const cls = ['ws-tl-day'];
        if (isToday) cls.push('today');
        if (isWeekend) cls.push('weekend');
        axisDays += `<div class="${cls.join(' ')}" style="min-width:${dayWidth}px"><div class="day-name">${dows[dow]}</div><div class="day-num">${months[d.getMonth()]} ${d.getDate()}</div></div>`;
        if (isToday) todayOffset = i;
      }
      let todayLineHtml = '';
      let nowTickHtml = '';
      if (todayOffset >= 0) {
        const left = labelWidth + todayOffset * dayWidth + (dayWidth / 2);
        todayLineHtml = `<div class="ws-tl-today-line" style="left:${left}px"></div>`;
        const minutesInDay = (now.getHours() * 60 + now.getMinutes());
        const dayProgress = minutesInDay / (24 * 60);
        const nowLeft = labelWidth + todayOffset * dayWidth + (dayProgress * dayWidth) - 4;
        nowTickHtml = `<div class="ws-tl-now-marker" style="left:${nowLeft}px" title="Now · ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}"></div>`;
      }
      const axisHtml = `<div class="ws-tl-axis"><div class="ws-tl-axis-label">When →</div><div class="ws-tl-axis-track">${axisDays}</div>${todayLineHtml}${nowTickHtml}</div>`;
      let groups: Array<{ id: string; label: string; desc: string; cls: string; steps: Array<OrchStep & { idx: number; eff: Date | null; parsed: ParsedWhen | null }> }>;
      if (laneMode === 'flat') {
        groups = [{ id: 'all', label: '', desc: '', cls: '', steps: plan.steps.map((s, i) => ({ ...s, idx: i, eff: effectiveDates[i], parsed: stepDates[i] })) }];
      } else if (laneMode === 'stage') {
        groups = WS_STAGE_ORDER.map((stageKey) => {
          const stage = WS_STAGES[stageKey];
          return { id: stageKey, label: stage.label, desc: stage.desc, cls: 'lane-' + stageKey, steps: plan.steps.map((s, i) => ({ ...s, idx: i, eff: effectiveDates[i], parsed: stepDates[i] })).filter((s) => _wsDeriveStage(s) === stageKey) };
        }).filter((g) => g.steps.length > 0);
      } else {
        groups = WS_OWNER_ORDER.map((ownerKey) => {
          const owner = WS_OWNERS[ownerKey];
          return { id: ownerKey, label: owner.label, desc: owner.desc, cls: 'lane-' + ownerKey, steps: plan.steps.map((s, i) => ({ ...s, idx: i, eff: effectiveDates[i], parsed: stepDates[i] })).filter((s) => _wsDeriveOwner(s) === ownerKey) };
        }).filter((g) => g.steps.length > 0);
      }
      let lanesHtml = '';
      for (const group of groups) {
        if (laneMode !== 'flat') {
          lanesHtml += `<div class="ws-tl-lane ${group.cls}">
            <div class="ws-tl-lane-hd">
              <span class="ws-tl-lane-name">${group.label}</span>
              <span class="ws-tl-lane-count">${group.steps.length} step${group.steps.length === 1 ? '' : 's'}</span>
              <span class="ws-tl-lane-desc">${group.desc}</span>
            </div>
            <div class="ws-tl-lane-body">`;
        }
        lanesHtml += '<div class="ws-tl-rows">';
        for (const sd of group.steps) lanesHtml += _wsTlBuildRow(sd, start, dayWidth, totalDays, today);
        lanesHtml += '</div>';
        if (laneMode !== 'flat') lanesHtml += '</div></div>';
      }
      tlEl.innerHTML = '<div class="ws-tl-grid">' + controlsHtml + axisHtml + lanesHtml + '</div>';
      // Wire lane mode buttons
      tlEl.querySelectorAll('#ws-tl-controls-bar [data-lane]').forEach((btn) => {
        btn.addEventListener('click', () => wsSetLaneMode((btn as HTMLElement).dataset.lane!));
      });
      // Wire bar interactions
      tlEl.querySelectorAll('.ws-tl-bar').forEach((bar) => _wsTlWireBar(bar as HTMLDivElement, dayWidth));
      // Now timer
      if (WS_TL_STATE.nowTimer) clearInterval(WS_TL_STATE.nowTimer);
      WS_TL_STATE.nowTimer = setInterval(() => {
        const strip = document.getElementById('ws-plan-strip');
        if (!strip || !strip.classList.contains('show') || !document.getElementById('ws-tl-view')?.classList.contains('show')) {
          clearInterval(WS_TL_STATE.nowTimer!); WS_TL_STATE.nowTimer = null; return;
        }
        const wrap = document.getElementById('ws-tl-view');
        const oldTick = wrap?.querySelector('.ws-tl-now-marker') as HTMLElement;
        if (oldTick && todayOffset >= 0) {
          const n = new Date();
          const minsIn = n.getHours() * 60 + n.getMinutes();
          const prog = minsIn / (24 * 60);
          const newLeft = labelWidth + todayOffset * dayWidth + (prog * dayWidth) - 4;
          oldTick.style.left = newLeft + 'px';
          oldTick.title = 'Now · ' + String(n.getHours()).padStart(2, '0') + ':' + String(n.getMinutes()).padStart(2, '0');
        }
      }, 60000);
    }

    function _wsTlBuildRow(sd: OrchStep & { idx: number; eff: Date | null; parsed: ParsedWhen | null }, start: Date, dayWidth: number, totalDays: number, today: Date): string {
      const i = sd.idx;
      const override = WS_TL_STATE.steps[i] || { dayOffset: 0, duration: 1 };
      const duration = override.duration || 1;
      let leftPx = 0, widthPx = (dayWidth - 8);
      const cls = ['ws-tl-bar'];
      let label = (sd.verb || ('Step ' + (i + 1))).slice(0, 18);
      cls.push('stage-' + _wsDeriveStage(sd));
      if (sd.eff) {
        const dayIdx = Math.round((sd.eff.getTime() - start.getTime()) / 86400000);
        leftPx = dayIdx * dayWidth + 4;
        widthPx = (duration * dayWidth) - 8;
        if (sd.eff.getTime() === today.getTime() && duration === 1) cls.push('is-today');
        if (sd.parsed && sd.parsed.isStandby) { cls.push('is-standby'); label = '⏸ ' + label; }
      } else {
        leftPx = (totalDays - 1) * dayWidth + 4;
        cls.push('is-unknown');
        label = '?  ' + label;
      }
      const verbEsc = (sd.verb || '').replace(/</g, '&lt;');
      const whenEsc = (sd.when || '—').replace(/</g, '&lt;');
      const whatEsc = (sd.what || '').replace(/</g, '&lt;');
      return `<div class="ws-tl-row">
        <div class="ws-tl-row-label">
          <div class="ws-tl-row-num">${sd.num || (i + 1)}</div>
          <div class="ws-tl-row-l">
            <div class="ws-tl-row-verb">${verbEsc}</div>
            <div class="ws-tl-row-when" title="${whenEsc}">${whenEsc}</div>
          </div>
        </div>
        <div class="ws-tl-row-track">
          <div class="${cls.join(' ')}" data-step="${i + 1}" data-step-idx="${i}" style="left:${leftPx}px;min-width:${Math.max(36, widthPx)}px" title="${verbEsc} — ${whatEsc}">
            <span class="bar-label">${label}</span>
            <div class="ws-tl-bar-resize" title="Drag right edge to extend duration"></div>
          </div>
        </div>
      </div>`;
    }

    function _wsTlWireBar(bar: HTMLDivElement, dayWidth: number) {
      bar.addEventListener('mousedown', (e) => {
        if ((e.target as HTMLElement).classList.contains('ws-tl-bar-resize')) { _wsTlBarStart(e, bar, dayWidth, 'resize'); return; }
        _wsTlBarStart(e, bar, dayWidth, 'move');
      });
      // Hover highlight
      bar.addEventListener('mouseenter', () => _wsHighlightStep(parseInt(bar.dataset.step!, 10)));
      bar.addEventListener('mouseleave', () => _wsClearHighlight());
    }

    function _wsTlBarStart(e: MouseEvent, bar: HTMLDivElement, dayWidth: number, mode: 'move' | 'resize') {
      e.preventDefault(); e.stopPropagation();
      const stepIdx = parseInt(bar.dataset.stepIdx!, 10);
      const stepNum = parseInt(bar.dataset.step!, 10);
      WS_TL_BAR_DRAG = { mode, stepIdx, stepNum, startX: e.clientX, initialLeft: parseInt(bar.style.left, 10) || 0, initialWidth: bar.offsetWidth, dayWidth, el: bar, moved: false, tip: null };
      bar.classList.add(mode === 'resize' ? 'resizing' : 'dragging');
      const tip = document.createElement('div');
      tip.className = 'ws-tl-drag-tip';
      document.body.appendChild(tip);
      WS_TL_BAR_DRAG.tip = tip;
      _wsTlBarUpdateTip(e.clientX, e.clientY);
      document.addEventListener('mousemove', _wsTlBarMove);
      document.addEventListener('mouseup', _wsTlBarEnd);
    }

    function _wsTlBarUpdateTip(x: number, y: number) {
      if (!WS_TL_BAR_DRAG || !WS_TL_BAR_DRAG.tip) return;
      const dx = x - WS_TL_BAR_DRAG.startX;
      const days = Math.round(dx / WS_TL_BAR_DRAG.dayWidth);
      if (WS_TL_BAR_DRAG.mode === 'move') {
        const sign = days > 0 ? '+' : '';
        WS_TL_BAR_DRAG.tip.textContent = '↔  ' + (days === 0 ? 'no change' : sign + days + ' day' + (Math.abs(days) === 1 ? '' : 's'));
      } else {
        const totalDur = Math.max(1, Math.round((WS_TL_BAR_DRAG.initialWidth + dx) / WS_TL_BAR_DRAG.dayWidth));
        WS_TL_BAR_DRAG.tip.textContent = '↔ duration: ' + totalDur + ' day' + (totalDur === 1 ? '' : 's');
      }
      WS_TL_BAR_DRAG.tip.style.left = (x + 14) + 'px';
      WS_TL_BAR_DRAG.tip.style.top = (y - 28) + 'px';
    }

    function _wsTlBarMove(e: MouseEvent) {
      if (!WS_TL_BAR_DRAG) return;
      const dx = e.clientX - WS_TL_BAR_DRAG.startX;
      if (Math.abs(dx) > 3) WS_TL_BAR_DRAG.moved = true;
      if (WS_TL_BAR_DRAG.mode === 'move') {
        WS_TL_BAR_DRAG.el.style.left = (WS_TL_BAR_DRAG.initialLeft + dx) + 'px';
      } else {
        const newW = Math.max(36, WS_TL_BAR_DRAG.initialWidth + dx);
        WS_TL_BAR_DRAG.el.style.minWidth = newW + 'px';
        WS_TL_BAR_DRAG.el.style.width = newW + 'px';
      }
      _wsTlBarUpdateTip(e.clientX, e.clientY);
    }

    function _wsTlBarEnd(e: MouseEvent) {
      if (!WS_TL_BAR_DRAG) return;
      const D = WS_TL_BAR_DRAG;
      D.el.classList.remove('dragging', 'resizing');
      if (D.tip) { D.tip.remove(); D.tip = null; }
      document.removeEventListener('mousemove', _wsTlBarMove);
      document.removeEventListener('mouseup', _wsTlBarEnd);
      if (!D.moved) { _wsClickStep(D.stepNum); WS_TL_BAR_DRAG = null; return; }
      const dx = e.clientX - D.startX;
      if (D.mode === 'move') {
        const dayShift = Math.round(dx / D.dayWidth);
        if (dayShift !== 0) {
          WS_TL_STATE.steps[D.stepIdx] = WS_TL_STATE.steps[D.stepIdx] || { dayOffset: 0, duration: 1 };
          WS_TL_STATE.steps[D.stepIdx].dayOffset = (WS_TL_STATE.steps[D.stepIdx].dayOffset || 0) + dayShift;
        }
      } else {
        const newDuration = Math.max(1, Math.round((D.initialWidth + dx) / D.dayWidth));
        WS_TL_STATE.steps[D.stepIdx] = WS_TL_STATE.steps[D.stepIdx] || { dayOffset: 0, duration: 1 };
        if (WS_TL_STATE.steps[D.stepIdx].duration !== newDuration) {
          WS_TL_STATE.steps[D.stepIdx].duration = newDuration;
        }
      }
      WS_TL_BAR_DRAG = null;
      _wsRenderTimeline(lastPlanRef.current);
    }

    function wsDismissPlanStrip() {
      const strip = document.getElementById('ws-plan-strip');
      if (strip) strip.classList.remove('show');
      if (WS_TL_STATE.nowTimer) { clearInterval(WS_TL_STATE.nowTimer); WS_TL_STATE.nowTimer = null; }
      _wsClearHighlight();
    }

    function _wsHighlightStep(stepNum: number) {
      const plan = lastPlanRef.current;
      if (!plan) return;
      const step = plan.steps[stepNum - 1];
      if (!step) return;
      WS_HIGHLIGHTED_STEP = stepNum;
      const refs = _wsParseStepRefs(step);
      document.querySelectorAll('.ws-note.ws-highlight').forEach((el) => el.classList.remove('ws-highlight'));
      for (const ref of refs) {
        const note = WS_NOTES.find((n) => n.sourceId === ref);
        if (note) { const el = document.querySelector('.ws-note[data-id="' + note.id + '"]'); if (el) el.classList.add('ws-highlight'); }
      }
      const keywords = _wsParseStepKeywords(step);
      if (keywords.length) {
        const stickyNotes = WS_NOTES.filter((n) => !n.kind || n.kind === 'note');
        for (const sn of stickyNotes) {
          const text = ((sn.title || '') + ' ' + (sn.body || '')).toLowerCase();
          let hits = 0;
          for (const kw of keywords) { if (text.includes(kw.toLowerCase())) hits++; }
          if (hits >= 2) { const el = document.querySelector('.ws-note[data-id="' + sn.id + '"]'); if (el) el.classList.add('ws-highlight'); }
        }
      }
      document.querySelectorAll('.ps-step').forEach((s) => s.classList.toggle('active', parseInt(s.getAttribute('data-step')!, 10) === stepNum));
      document.querySelectorAll('.ws-tl-bar').forEach((b) => b.classList.toggle('active', parseInt(b.getAttribute('data-step')!, 10) === stepNum));
      _wsRenderConnectors();
    }

    function _wsClearHighlight() {
      WS_HIGHLIGHTED_STEP = null;
      document.querySelectorAll('.ws-note.ws-highlight').forEach((el) => el.classList.remove('ws-highlight'));
      document.querySelectorAll('.ps-step').forEach((s) => s.classList.remove('active'));
      document.querySelectorAll('.ws-tl-bar').forEach((b) => b.classList.remove('active'));
      _wsRenderConnectors();
    }

    function _wsClickStep(stepNum: number) {
      if (WS_HIGHLIGHTED_STEP === stepNum && document.querySelector('.ps-step.active')) _wsClearHighlight();
      else _wsHighlightStep(stepNum);
    }

    // ── keyboard + paste ──
    function onKeydown(e: KeyboardEvent) {
      const inField = e.target && ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable || ((e.target as HTMLElement).closest && (e.target as HTMLElement).closest('[contenteditable="true"]')));
      if (inField) return;
      if ((e.key === 'c' || e.key === 'C') && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); wsToggleConnectMode(); return; }
      if (e.key === 'Escape' && WS_CONNECT_MODE) { e.preventDefault(); wsToggleConnectMode(); }
    }

    let _wsPasteHintTimer: ReturnType<typeof setTimeout> | null = null;
    function _wsShowPasteHint(len: number, truncated: boolean) {
      document.querySelectorAll('.ws-paste-hint').forEach((el) => el.remove());
      if (_wsPasteHintTimer) clearTimeout(_wsPasteHintTimer);
      const hint = document.createElement('div');
      hint.className = 'ws-paste-hint';
      hint.innerHTML = '<span class="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3.5" y="3" width="9" height="11.5" rx="1"/><rect x="5.5" y="1.5" width="5" height="2.5" rx=".5"/><path d="M6 7h4M6 9.5h4M6 12h3"/></svg></span>&nbsp;&nbsp;Paste captured as a sticky note · ' + len + ' chars' + (truncated ? ' (truncated)' : '');
      document.body.appendChild(hint);
      _wsPasteHintTimer = setTimeout(() => { (hint as HTMLElement).style.transition = 'opacity .3s'; (hint as HTMLElement).style.opacity = '0'; setTimeout(() => hint.remove(), 320); }, 2400);
    }

    function onPaste(e: ClipboardEvent) {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable || (t.closest && t.closest('[contenteditable="true"]')))) return;
      const text = (e.clipboardData || window.clipboardData)?.getData('text/plain') || '';
      if (!text || !text.trim()) return;
      e.preventDefault();
      let body = text.trim();
      let truncated = false;
      if (body.length > 800) { body = body.substring(0, 800) + '… [truncated]'; truncated = true; }
      let title = '';
      const firstNewline = body.indexOf('\n');
      if (firstNewline > 0 && firstNewline < 60) { title = body.substring(0, firstNewline).trim(); body = body.substring(firstNewline + 1).trim(); }
      else if (body.length < 60) { title = body; body = ''; }
      else { title = 'Pasted ' + new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
      const offset = (WS_NOTES.length % 12) * 26;
      const item: WSNote = { id: WS_NEXT_ID++, kind: 'note', color: WS_SELECTED_COLOR || 'yellow', x: 40 + offset, y: 40 + offset, w: 240, z: ++WS_TOP_Z, title, body, ts: new Date().toISOString() };
      WS_NOTES.push(item);
      const board = document.getElementById('ws-board');
      const empty = document.getElementById('ws-empty');
      if (empty) (empty as HTMLElement).style.display = 'none';
      if (board) { const el = _wsBuildNote(item, true); board.appendChild(el); }
      _wsUpdateCounters();
      wsSave();
      _wsShowPasteHint(text.length, truncated);
    }

    // ── boot ──
    wsLoad();
    wsRender();
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('paste', onPaste);

    // Bridge for JSX click handlers
    apiRef.current = {
      wsAddNote: () => wsAddNote(),
      wsClearAll: () => wsClearAll(),
      wsSelectColor: (color: unknown, el: unknown) => wsSelectColor(color as string, el as HTMLElement),
      wsToggleConnectMode: () => wsToggleConnectMode(),
      wsOrchestrate: () => { wsOrchestrate(); },
      wsLoadDemoScenario: () => wsLoadDemoScenario(),
      wsSwitchPlanView: (view: unknown) => wsSwitchPlanView(view as string),
      wsDismissPlanStrip: () => wsDismissPlanStrip(),
      wsOpenPicker: (kind: unknown) => wsOpenPicker(kind as string),
      wsQuickCreate: (kind: unknown) => wsQuickCreate(kind as string),
    };

    return () => {
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('paste', onPaste);
      if (WS_TL_STATE.nowTimer) { clearInterval(WS_TL_STATE.nowTimer); WS_TL_STATE.nowTimer = null; }
      if (_wsPasteHintTimer) clearTimeout(_wsPasteHintTimer);
      document.body.classList.remove('ws-connect-mode');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMo, closeMo, showToast]);

  const call = (fn: string) => (...args: unknown[]) => { const f = apiRef.current[fn]; if (f) f(...args); };

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Shared whiteboard for notes, reminders, and rough thinking</p>
            <h1 className="pagehead__title">My Workspace</h1>
            <div className="pagehead__facts">
              <span className="fact">Visible to everyone signed in · persists across sessions</span>
            </div>
          </div>
          <div className="pagehead__actions" id="ws-header-actions">
            <button className="btn btn--sm" onClick={call('wsClearAll')} title="Remove all notes">↺ Clear board</button>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="ws-toolbar">
          <div className="ws-tb-l">
            <button className="btn btn--primary btn--sm" onClick={call('wsAddNote')}><span style={{ fontSize: '13px', lineHeight: 1 }}>＋</span>&nbsp;Note</button>
            <div className="ws-color-swatches" id="ws-swatches">
              {WS_COLORS.map((c) => (
                <div key={c} className={`ws-swatch c-${c}${c === 'yellow' ? ' selected' : ''}`} data-color={c} onClick={(e) => call('wsSelectColor')(c, e.currentTarget)} title={c[0].toUpperCase() + c.slice(1)} role="button" tabIndex={0}></div>
              ))}
            </div>
            <div className="ws-tb-divider"></div>
            <button className="btn btn--sm" onClick={() => call('wsOpenPicker')('jira')}><span style={{ color: '#0052CC', fontWeight: 700 }}>＋</span>&nbsp;Jira</button>
            <button className="btn btn--sm" onClick={() => call('wsOpenPicker')('task')}><span style={{ color: 'var(--navy)', fontWeight: 700 }}>＋</span>&nbsp;Task</button>
            <button className="btn btn--sm" onClick={() => call('wsOpenPicker')('risk')}><span style={{ color: '#A32B33', fontWeight: 700 }}>＋</span>&nbsp;Risk</button>
            <div className="ws-tb-divider"></div>
            <button className="btn btn--sm btn-connect-mode" id="ws-connect-btn" onClick={call('wsToggleConnectMode')} title="Toggle connect mode (shortcut: C)"><span style={{ fontWeight: 700, fontSize: '13px' }}>⇄</span>&nbsp;Connect&nbsp;<kbd style={{ fontSize: '9px', fontFamily: 'var(--mono)', background: 'var(--surface-sub)', color: 'var(--ink-45)', padding: '1px 4px', borderRadius: '2px', border: '1px solid var(--hair-strong)', fontWeight: 700, marginLeft: '2px' }}>C</kbd></button>
          </div>
          <div className="ws-tb-r">
            <button className="btn-orchestrator" onClick={call('wsOrchestrate')} id="ws-orch-btn" title="Course-of-action recommendation"><span className="orch-spark"><span className="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M8 1.5l1.7 4.8L14.5 8l-4.8 1.7L8 14.5l-1.7-4.8L1.5 8l4.8-1.7z" fill="currentColor" /></svg></span></span>&nbsp;Orchestrator</button>
            <button className="btn btn--sm" onClick={call('wsLoadDemoScenario')} style={{ color: 'var(--gold)', borderColor: 'var(--gold)', background: 'var(--gold-wash)' }} title="Load a realistic demo">▶&nbsp;Try the demo</button>
            <span id="ws-counts" style={{ fontSize: '11px', color: 'var(--ink-45)', fontFamily: 'var(--mono)', marginLeft: '6px' }}><span id="ws-note-count">0</span> items · saved <span id="ws-last-saved" style={{ color: 'var(--ink)', fontWeight: 700 }}>—</span></span>
          </div>
        </div>

        {/* Plan strip */}
        <div className="ws-plan-strip" id="ws-plan-strip">
          <div className="ps-header">
            <span className="ps-spark"><span className="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 1.5l1.7 4.8L14.5 8l-4.8 1.7L8 14.5l-1.7-4.8L1.5 8l4.8-1.7z" fill="currentColor" /></svg></span></span>
            <span className="ps-title" id="ws-ps-title">Plan</span>
            <div className="ps-toggle" id="ws-ps-toggle">
              <button className="active" data-view="steps" onClick={() => call('wsSwitchPlanView')('steps')}>Steps</button>
              <button data-view="timeline" onClick={() => call('wsSwitchPlanView')('timeline')}>Timeline</button>
            </div>
            <span className="ps-hint" id="ws-ps-hint">Hover a step → cards light up · click → details</span>
            <div className="ps-actions">
              <button className="btn" onClick={call('wsOrchestrate')} title="Re-run with current workspace contents"><span className="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 8a6 6 0 1 1-2-4.5" /><path d="M14 2v3.5h-3.5" /></svg></span> Re-run</button>
            </div>
            <button className="ps-x" onClick={call('wsDismissPlanStrip')} title="Dismiss strip" aria-label="Dismiss plan strip"><span className="ico"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 3l10 10M13 3L3 13"/></svg></span></button>
          </div>
          <div className="ps-steps" id="ws-ps-steps"></div>
          <div className="ws-tl" id="ws-tl-view"></div>
        </div>

        {/* Board */}
        <div className="ws-board-wrap">
          <div className="ws-board" id="ws-board">
            <svg className="ws-connectors-layer" id="ws-connectors-svg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <marker id="ws-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#16202E" /></marker>
                <marker id="ws-arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#A32B33" /></marker>
                <marker id="ws-arrow-gold" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#1D5FA8" /></marker>
              </defs>
            </svg>
            <div className="ws-empty" id="ws-empty" style={{ display: 'block' }}>
              Your board is empty.
              <div className="em-sub">ADD NOTES, JIRA TICKETS, TASKS, OR RISKS · DRAG TO REPOSITION · CLICK <strong style={{ color: 'var(--gold)' }}>ORCHESTRATOR</strong> FOR A RECOMMENDED COURSE OF ACTION · OR TRY THE DEMO<br /><span style={{ fontSize: '10px', letterSpacing: '.08em', opacity: .75, marginTop: '8px', display: 'inline-block' }}>SHORTCUTS · <kbd style={{ background: 'rgba(0,0,0,.06)', padding: '1px 5px', borderRadius: '2px', fontFamily: 'var(--mono)', fontSize: '10px' }}>C</kbd> CONNECT MODE · <kbd style={{ background: 'rgba(0,0,0,.06)', padding: '1px 5px', borderRadius: '2px', fontFamily: 'var(--mono)', fontSize: '10px' }}>⌘V</kbd> PASTE A NOTE FROM CLIPBOARD</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal overlay */}
      {modal.open && (
        <div className="ws-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeMo(); }}>
          <div className={`ws-modal-card ${modal.size === 'xl' ? 'xl' : 'lg'}`}>
            <div className="ws-modal-head" dangerouslySetInnerHTML={{ __html: modal.title }} />
            <div className="ws-modal-body" dangerouslySetInnerHTML={{ __html: modal.body }} />
            <div className="ws-modal-foot" dangerouslySetInnerHTML={{ __html: modal.footer }} />
          </div>
        </div>
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="ws-toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`ws-toast ws-toast--${t.type}`}>{t.msg}</div>
          ))}
        </div>
      )}
    </>
  );
}

export default WorkspacePage;
